<script>
import baseMixin from '@/mixins/baseMixin'
import { mapActions, mapGetters } from 'vuex'
import DataPoint from '@/models/general/dataPoint'
import dayjs from '@/services/date/index'
import DateFormatToken from '@/constants/date/DateFormatToken'
import StatisticsBreakdownCard from '../general/statistics-breakdown-card.vue'

export default {
  name: 'DisputeTableInvoiceCenterStatsBreakdownCard',
  components: { StatisticsBreakdownCard },
  mixins: [baseMixin],
  props: {},
  data: () => {
    return {
      isGraphLoading: false,
      invoiceCenterId: null,
      graphWidth: 160,
      date: null,
    }
  },
  computed: {
    ...mapGetters({
      invoiceCenters: 'disputes/invoiceCenters',
      invoiceCenter: 'disputes/invoiceCenter',
    }),
    hasSelectedInvoiceCenter() {
      return !!this.invoiceCenterId
    },
    invoiceCenterGraphData() {
      const data = this.invoiceCenter(this.invoiceCenterId)

      const dataPoints = [
        new DataPoint({
          key: 'invoice_center_stats_not_submitted',
          title: 'Not Submitted',
          value: data?.pendingCount ?? 0,
          showSummary: true,
          showOnGraph: true,
          color: '#FF6384', // Red
        }),
        new DataPoint({
          key: 'invoice_center_stats_candi_submitted',
          title: 'Candidate Submitted',
          value: data?.candidateCount ?? 0,
          showSummary: true,
          showOnGraph: true,
          color: '#FFCE56', // Yellow
        }),
        new DataPoint({
          key: 'invoice_center_stats_client_submitted',
          title: 'Client Submitted',
          value: data?.clientCount ?? 0,
          showSummary: true,
          showOnGraph: true,
          color: '#00ADEF', // Blue
        }),
      ]

      return dataPoints
    },
    hasGraphData() {
      return this.invoiceCenterGraphData.length > 0
    },
    compGraphOptions() {
      return {
        cutOut: 30,
        layout: {
          padding: {
            left: this.graphWidth / 2,
          },
        },
      }
    },
    /**
     * Returns the date of last friday (relative to the current date)
     * @returns Last Friday's date e.g. 2024-07-12
     */
    getLastFridaysDate() {
      const today = new Date()

      const t =
        today.getDate() +
        (6 - today.getDay() - 1) -
        (today.getDay() === 6 ? 0 : 7)
      const lastFriday = new Date()
      lastFriday.setDate(t)

      return dayjs(lastFriday).format(DateFormatToken.YYYYMMDD)
    },
    maxDate() {
      return dayjs().format(DateFormatToken.YYYYMMDD)
    },
  },
  created() {
    this.date = this.getLastFridaysDate
  },
  methods: {
    ...mapActions({
      getInvoiceCenterDetails: 'disputes/getInvoiceCenterDisputesData',
    }),
    async loadInvoiceCenterDetails() {
      if (!this.date || !this.hasSelectedInvoiceCenter) return

      this.isGraphLoading = true

      await this.getInvoiceCenterDetails({
        invoiceCenterId: this.invoiceCenterId,
        dateUntil: this.date,
      })

      this.isGraphLoading = false
    },
    async datePickerInput(value) {
      if (this.date === value) return
      this.date = value

      await this.loadInvoiceCenterDetails()
    },
  },
}
</script>

<template>
  <StatisticsBreakdownCard
    class="h-100"
    :flat="true"
    transparent
    outlined
    :data-points="invoiceCenterGraphData"
    :graph-options="compGraphOptions"
  >
    <template v-slot:header>
      <v-autocomplete
        v-model="invoiceCenterId"
        :items="invoiceCenters"
        item-text="name"
        dense
        solo
        flat
        clearable
        background-color="#00000000"
        item-value="id"
        prepend-icon="mdi-bank"
        label="Filter by invoice center"
        hide-details
        @change="loadInvoiceCenterDetails"
      />

      <v-text-field
        prefix="Date until:"
        dense
        hide-details
        solo
        flat
        background-color="#00000000"
        :value="date"
        :max="maxDate"
        type="date"
        @input="datePickerInput"
      />
    </template>
    <template v-slot:footer>
      <v-btn
        class="py-2"
        color="primary"
        small
        text
        :loading="isGraphLoading"
        :disabled="!hasSelectedInvoiceCenter"
        @click="$emit('openInvoiceCenterFlyout', { invoiceCenterId, date })"
        >Bulk Actions</v-btn
      >
    </template>
  </StatisticsBreakdownCard>
</template>
